import { cacheUpdatePreferredContactMethodReminderAt } from '@/graphql/cache/cache-mutations';
import {
  ContactMessagingTypeEnum,
  GetPreferredContactMethodsDocument,
  useGetPreferredContactMethodsQuery,
  useSetWorkspacePreferredContactMethodsLaterMutation,
  useSetWorkspacePreferredContactMethodsMutation,
  useworkspaceAuthMeV2Query,
} from '@/types/generated/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import { ROLE_MAPS } from '@shared/constants/roles';
import { useModal } from '@shared/hooks/use-modal';
import { prefixPlus } from '@shared/utils';
import { hasRoleAccess } from '@shared/utils/roles/has-role-access.helper';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { TPreferredContactForm } from '../../banner.types';
import { PreferredContactSchema } from '../schema/preferred-contact.schema';

export const usePreferredContact = () => {
  const { open, handleOpen, handleClose } = useModal();
  const defaultValues = {
    email: '',
    messaging: '',
    messagingType: 'whatsapp',
    phoneCall: '',
    timeRange: ['09:00:00', '18:00:00'] as [string, string],
    emailEnabled: false,
    messagingEnabled: false,
    phoneCallEnabled: false,
    contactMethods: undefined,
  };
  const {
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<TPreferredContactForm>({
    resolver: zodResolver(PreferredContactSchema),
    defaultValues,
  });

  const selectedMethods = {
    email: watch('emailEnabled'),
    messaging: watch('messagingEnabled'),
    phoneCall: watch('phoneCallEnabled'),
  };

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const [hasPreferredContactRoleAccess] = hasRoleAccess(ROLE_MAPS.SETTINGS_MYWORKSPACE_SETPREFEREDCONTACT);

  const { data: preferredContactMethodsData } = useGetPreferredContactMethodsQuery({
    skip: !hasPreferredContactRoleAccess,
  });
  const preferredContactMethodData = preferredContactMethodsData?.getPreferredContactMethods?.data;
  const { data: wksData } = useworkspaceAuthMeV2Query({
    skip: !hasPreferredContactRoleAccess,
  });

  const preferredContactMethodReminderAt = wksData?.workspaceAuthMeV2?.data?.preferredContactMethodReminderAt;

  const hasPreferredContactMethod = Boolean(
    preferredContactMethodData?.emailEnabled ||
    preferredContactMethodData?.messagingEnabled ||
    preferredContactMethodData?.phoneCallEnabled
  );

  useEffect(() => {
    if (!open || !preferredContactMethodData) {
      return;
    }
    reset({
      emailEnabled: preferredContactMethodData.emailEnabled ?? false,
      email: preferredContactMethodData.email ?? '',
      messagingEnabled: preferredContactMethodData.messagingEnabled ?? false,
      messagingType: preferredContactMethodData.messagingType ?? 'whatsapp',
      messaging: preferredContactMethodData.messagingNumber ?? '',
      phoneCallEnabled: preferredContactMethodData.phoneCallEnabled ?? false,
      phoneCall: preferredContactMethodData.phoneCallNumber ?? '',
      contactMethods: undefined,
      timeRange: [
        preferredContactMethodData.prefStartTime ?? '09:00:00',
        preferredContactMethodData.prefEndTime ?? '18:00:00',
      ],
    });
  }, [open, hasPreferredContactRoleAccess, hasPreferredContactMethod, preferredContactMethodData, reset]);

  const [setWorkspacePreferredContactMethods] = useSetWorkspacePreferredContactMethodsMutation({
    onCompleted: (data) => {
      notification.success({
        message: data?.setWorkspacePreferredContactMethods?.message,
      });
    },

    onError: (error) => {
      notification.error({
        message: error?.message,
      });
    },
  });

  const [setWorkspacePreferredContactMethodsLater] = useSetWorkspacePreferredContactMethodsLaterMutation({
    onCompleted: (data) => {
      notification.success({
        message: data?.setWorkspacePreferredContactMethodsLater?.message,
      });
    },

    onError: (error) => {
      notification.error({
        message: error?.message,
      });
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    await setWorkspacePreferredContactMethods({
      variables: {
        input: {
          prefStartTime: data.timeRange?.[0] ?? '09:00:00',
          prefEndTime: data.timeRange?.[1] ?? '18:00:00',
          emailEnabled: data.emailEnabled,
          email: data.emailEnabled ? data.email : null,
          messagingEnabled: data.messagingEnabled,
          messagingType: data.messagingEnabled ? (data.messagingType as ContactMessagingTypeEnum) : null,
          messagingNumber: data.messagingEnabled ? prefixPlus(data.messaging) : null,
          phoneCallEnabled: data.phoneCallEnabled,
          phoneCallNumber: data.phoneCallEnabled ? prefixPlus(data.phoneCall) : null,
        },
      },
      refetchQueries: [{ query: GetPreferredContactMethodsDocument }],
      awaitRefetchQueries: true,
    });

    const reminderAt = dayjs().add(1, 'month').toISOString();
    cacheUpdatePreferredContactMethodReminderAt(reminderAt);

    handleClose();
  });

  const handleLater = async () => {
    await setWorkspacePreferredContactMethodsLater();
    const reminderAt = dayjs().add(1, 'month').toISOString();
    cacheUpdatePreferredContactMethodReminderAt(reminderAt);
  };

  return {
    open,
    handleOpen,
    handleClose,
    control,
    selectedMethods,
    setValue,
    trigger,
    onSubmit,
    clearErrors,
    errors,
    handleLater,
    data: preferredContactMethodsData,
    preferredContactMethodReminderAt,
    hasPreferredContactMethod,
    hasPreferredContactRoleAccess,
  };
};
